import EventEmitter from "eventemitter3";
import { DateLike } from "~/lib/date";
import { parse as parseJobLog } from "~/lib/job-log";
import { getJson } from "~/lib/http";

import JobLogGroupStore from "~/stores/JobLogGroupStore";

type LogResponse = {
  streaming: boolean;
  bytes: number;
  max_bytes: number;
  header_times: Array<unknown>;
  output: string;
  truncated: boolean;
  started_at_from_agent?: string;
  finished_at_from_agent?: string;
};

export type JobPartial = {
  id: string;
  basePath: string;
  state: string;
  startedAt: DateLike | null | undefined;
};

export type Group = {
  id: string;
  name: string;
  textContent: string;
  expanded: boolean;
  deemphasized?: boolean;
  lines: Array<{
    id: string;
    number: number;
    line: string;
    startedAt: DateLike | null | undefined;
    textContent: string;
  }>;
  number: number;
  startedAt: DateLike | null | undefined;
  finished: boolean;
  finishedAt: DateLike | null | undefined;
  durationPercentage: number | null | undefined;
  // Set only on the currently-running group: the longest finished group's
  // duration (ms), used as the denominator to grow its bar live.
  maxDuration?: number | null;
  bootstrap?: boolean;
};

export type Job = {
  id: string;
  expanded?: boolean;
  url: string;
  passed?: boolean;
  groups: Array<Group>;
  headerTimes: Array<unknown>;
  bytes: number;
  maxBytes: number;
  totalLines: number;
  streaming: boolean;
  loaded: boolean;
  sshKeyFailure?: boolean;
  httpsAuthFailure?: boolean;
  dockerRateLimited?: boolean;
  truncated: boolean;
  startedAtFromAgent?: string;
  finishedAtFromAgent?: string;
  gitCommitNotFoundInBranchFailure?: boolean;
  secretsAccessFailure?: boolean;
  linesHaveTimestamps?: boolean;
  lineNumberToGroupNumber?: Map<number, number>;
  // The id of the group currently force-expanded because it's the running
  // group, so we can revert it when the running group moves on.
  autoExpandedGroupId?: string | null;
  // The header-defined expanded state captured when we force-expanded the
  // running group, so we revert to that rather than the state from a later
  // (e.g. finished) parse, whose fallbacks can differ from the header.
  autoExpandedGroupExpanded?: boolean | null;
  _timeout?: number | null | undefined;
};

const createLog = (job: JobPartial): Job => ({
  id: job.id,
  url: job.basePath + "/log",
  groups: [],
  headerTimes: [],
  bytes: 0,
  maxBytes: 0,
  totalLines: 0,
  streaming: false,
  loaded: false,
  truncated: false,
});

class JobLogStore {
  eventEmitter: EventEmitter;
  state: {
    [key: string]: Job;
  };

  constructor() {
    this.eventEmitter = new EventEmitter();
    this.state = {};
  }

  addChangeListener(jobId: string, callback: any) {
    return this.eventEmitter.addListener(`${jobId}/change`, callback);
  }

  removeChangeListener(jobId: string, callback: any) {
    return this.eventEmitter.removeListener(`${jobId}/change`, callback);
  }

  delete(jobId: string) {
    delete this.state[jobId];
    return this.eventEmitter.emit(`${jobId}/change`);
  }

  get(jobId: string) {
    return this.state[jobId];
  }

  /**
   * Get the group number that contains a given line number.
   * Returns undefined if the log isn't loaded or the line doesn't exist.
   */
  getGroupNumberForLine(jobId: string, lineNumber: number): number | undefined {
    const log = this.get(jobId);
    return log?.lineNumberToGroupNumber?.get(lineNumber);
  }

  getGroupEndLine(jobId: string, groupNumber: number): number | undefined {
    const log = this.get(jobId);
    const group = log?.groups.find((grp) => grp.number === groupNumber);
    if (!group) {
      return undefined;
    }
    const lastLine = group.lines[group.lines.length - 1]?.number;
    if (lastLine === undefined) {
      return undefined;
    }
    return log.totalLines ? Math.min(lastLine, log.totalLines) : lastLine;
  }

  refresh(jobId: string) {
    const log = this.get(jobId);

    return getJson<LogResponse>(log.url).then((json) => {
      // Save some of log data
      log.streaming = json.streaming;
      log.bytes = json.bytes;
      log.maxBytes = json.max_bytes;
      log.headerTimes = json.header_times;
      log.sshKeyFailure = false;
      log.httpsAuthFailure = false;
      log.dockerRateLimited = false;
      log.secretsAccessFailure = false;
      log.truncated = json.truncated;
      log.startedAtFromAgent = json.started_at_from_agent;
      log.finishedAtFromAgent = json.finished_at_from_agent;

      let previousGroups: Group[] = [];

      // Parse the output if we have any
      if (json.output !== "") {
        previousGroups = log.groups;
        const parsed = parseJobLog(log, json.output);
        log.totalLines = parsed.totalLines;
        log.groups = parsed.groups;
        log.sshKeyFailure = parsed.sshKeyFailure;
        log.httpsAuthFailure = parsed.httpsAuthFailure;
        log.dockerRateLimited = parsed.dockerRateLimited;
        log.gitCommitNotFoundInBranchFailure = parsed.gitCommitNotFoundInBranchFailure;
        log.secretsAccessFailure = parsed.secretsAccessFailure;
        log.linesHaveTimestamps = parsed.linesHaveTimestamps;
        log.lineNumberToGroupNumber = parsed.lineNumberToGroupNumber;
      }

      // If the parser identified any groups that should be automatically
      // expanded, set the default expands for them.
      for (const group of Array.from(log.groups)) {
        JobLogGroupStore.setDefault(group.id, group.expanded);
      }

      this.preserveTruncatedGroupState(log, previousGroups);

      // While the job streams, keep the running log group expanded so its output
      // stays visible.
      this.updateAutoExpandedGroup(log);

      // Mark the log as loaded
      log.loaded = true;

      // Emit the change so the components can reload
      this.eventEmitter.emit(`${log.id}/change`);

      // Reload the log if it needs to be streamed to the client
      if (log.streaming) {
        // Don't set a timeout if one has already been set
        if (log._timeout) {
          return;
        }

        // Schedule a refresh of the log in 5 seconds
        return (log._timeout = setTimeout(() => {
          this.refresh(log.id);
          return (log._timeout = null);
        }, 5000));
      }
    });
  }

  private preserveTruncatedGroupState(log: Job, previousGroups: Group[]) {
    if (!log.streaming || !log.truncated || previousGroups.length === 0) {
      return;
    }

    const currentGroupNameById = new Map(log.groups.map((group) => [group.id, group.name]));
    const previousGroupNameById = new Map(previousGroups.map((group) => [group.id, group.name]));
    const previousExpandedByName = new Map<string, boolean[]>();
    for (const group of previousGroups) {
      if (
        !group.name ||
        group.id === log.autoExpandedGroupId ||
        currentGroupNameById.get(group.id) === group.name
      ) {
        continue;
      }

      const expanded = JobLogGroupStore.isExpanded(group.id);
      if (expanded === undefined) {
        continue;
      }

      const expandedStates = previousExpandedByName.get(group.name) ?? [];
      expandedStates.push(expanded);
      previousExpandedByName.set(group.name, expandedStates);
    }

    for (const group of log.groups) {
      if (!group.name || previousGroupNameById.get(group.id) === group.name) {
        continue;
      }

      const expandedStates = previousExpandedByName.get(group.name);
      const expanded = expandedStates?.shift();
      if (expanded !== undefined) {
        JobLogGroupStore.set(group.id, expanded);
      }
    }
  }

  // Keep the running log group force-expanded while the job streams so its
  // output stays visible, reverting it once the job moves on. Behind a feature
  // flag while we test it in production.
  //
  // We only act on transitions (the running group changing, or disappearing
  // when the job finishes/truncates), so a user's manual toggle of the running
  // group is preserved while it remains the running group.
  private updateAutoExpandedGroup(log: Job) {
    if (!window.Features?.AutoExpandRunningLogGroup) {
      return;
    }

    // Only force-expand the running group on the new build experience, which
    // virtualises log output (VirtualisedLogOutput). The classic build page
    // renders every group in markup, so keeping the streaming running group
    // expanded re-renders every group on each poll causing performance issues.
    if (!window.Features?.BuildSidebar) {
      return;
    }

    const runningGroup = this.runningGroup(log);
    const runningGroupId = runningGroup ? runningGroup.id : null;

    if (runningGroupId !== log.autoExpandedGroupId) {
      // Revert the group we previously expanded to the header-defined state we
      // captured when expanding it. We use the captured value rather than this
      // parse's state (the finished, non-streaming parse expands the last group
      // as a fallback, which would otherwise leave a header-collapsed running
      // group open after completion) and don't depend on finding the group, so
      // we also clear the forced state when a log becomes truncated and its
      // line-number-based IDs no longer line up.
      if (log.autoExpandedGroupId) {
        JobLogGroupStore.set(log.autoExpandedGroupId, log.autoExpandedGroupExpanded ?? false);
      }
      if (runningGroup) {
        JobLogGroupStore.set(runningGroup.id, true);
      }
      log.autoExpandedGroupId = runningGroupId;
    }

    // Track the running group's current header-defined state (without re-forcing
    // it, so a manual collapse sticks) so the eventual revert restores the
    // latest `^^^ +++`/`^^^ ---` directive rather than a stale value.
    log.autoExpandedGroupExpanded = runningGroup ? runningGroup.expanded : null;
  }

  // The running group is the last group, but only for non-truncated streaming
  // logs. Truncated logs derive group IDs from line numbers that shift on every
  // poll while tailing (see `createGroup` in `app/javascript/lib/job-log.ts`),
  // so the running group looks "new" each refresh; and a finished log has no
  // running group. In both cases we return null so we never force-expand.
  //
  // The parser marks the running group with `maxDuration`, but that depends on
  // timing data (`header_times`) that can lag a poll behind the streamed output,
  // so we fall back to the last group to avoid hiding live output for a poll.
  private runningGroup(log: Job): Group | null {
    if (log.truncated || !log.streaming || log.groups.length === 0) {
      return null;
    }
    return (
      log.groups.find((group) => group.maxDuration != null) ?? log.groups[log.groups.length - 1]
    );
  }

  load(job: JobPartial) {
    // Find or create the log
    const { id } = job;

    // Create the log record if it doesn't exist.
    // The log data won't be loaded until refreshed, e.g. when JobLogOutput is mounted.
    if (!this.state[id]) {
      this.state[id] = createLog(job);
    }

    return this.state[id];
  }
}

export default new JobLogStore();
